/**
 * Verbes métier du domaine devis.
 *
 * Ce sont exactement les outils que le serveur MCP exposera : des verbes
 * nommés dans la langue du métier, pas du CRUD. `lister_devis_en_attente` dit
 * quelque chose à un agent comme à un patron ; `GET /quotes?status=sent` ne
 * dit rien à personne.
 */
import { ActionLevel } from "../../levels.js";
import { DomainError } from "../../errors.js";
import { defineService } from "../../service.js";
import type { QuoteRepository } from "./repository.js";
import { computeQuoteTotals } from "./totals.js";
import { EDITABLE_STATUSES } from "./types.js";
import type { QuoteDetail, QuoteSummary } from "./types.js";
import type {
  AnnounceAvailabilityInput,
  ChangeQuoteStatusInput,
  ListQuotesInput,
  ReadQuoteInput,
  SaveQuoteInput,
} from "./schema.js";

export function createQuoteServices(repo: QuoteRepository) {
  const listerDevis = defineService<ListQuotesInput, QuoteSummary[]>({
    name: "lister_devis",
    level: ActionLevel.Read,
    handler: (ctx, input) =>
      repo.list(ctx, {
        status: input.status,
        clientId: input.clientId,
        expiredOnly: input.expiredOnly,
      }),
  });

  const listerDevisEnAttente = defineService<
    { confirmationId?: string },
    QuoteSummary[]
  >({
    name: "lister_devis_en_attente",
    level: ActionLevel.Read,
    handler: (ctx) => repo.list(ctx, { status: ["sent", "viewed"] }),
  });

  const lireDevis = defineService<ReadQuoteInput, QuoteDetail>({
    name: "lire_devis",
    level: ActionLevel.Read,
    handler: async (ctx, input) => {
      const quote = await repo.findById(ctx, input.quoteId);
      if (!quote) {
        throw new DomainError("not_found", "Ce devis n'existe pas.", {
          quoteId: input.quoteId,
        });
      }
      return quote;
    },
  });

  const enregistrerDevis = defineService<SaveQuoteInput, QuoteDetail>({
    name: "enregistrer_devis",
    level: ActionLevel.ReversibleWrite,
    handler: async (ctx, input) => {
      // Le taux réduit s'applique à tout le devis : on l'inscrit sur chaque
      // ligne plutôt que de le garder en surcharge d'en-tête. Sans ça, le
      // domaine calculerait la TVA à 10 % pendant que le trigger Postgres,
      // qui ne lit que `quote_lines.tva_rate`, la recalculerait à 20 % — deux
      // sources de vérité pour le montant qu'on présente au client.
      const effectiveRate = input.appliesReducedTva ? input.reducedTvaRate : undefined;

      const lines = input.lines.map((l) => ({
        id: l.id,
        isSectionHeader: l.isSectionHeader,
        sectionTitle: l.sectionTitle ?? null,
        designation: l.designation,
        unit: l.unit,
        quantity: l.quantity,
        unitCost: l.unitCost,
        unitPrice: l.unitPrice,
        tvaRate: effectiveRate ?? l.tvaRate,
        notes: l.notes ?? null,
      }));

      // Un devis déjà envoyé ne se modifie pas en place : le client a reçu un
      // document chiffré, le corriger en silence le rendrait faux sans trace.
      // La marche à suivre est une nouvelle version.
      if (input.quoteId) {
        const existing = await repo.findById(ctx, input.quoteId);
        if (!existing) {
          throw new DomainError("not_found", "Ce devis n'existe pas.", {
            quoteId: input.quoteId,
          });
        }
        if (!EDITABLE_STATUSES.includes(existing.status)) {
          throw new DomainError(
            "conflict",
            `Ce devis est au statut « ${existing.status} » et n'est plus modifiable. ` +
              `Créez une nouvelle version plutôt que de le corriger.`,
            { quoteId: input.quoteId, status: existing.status },
          );
        }
      }

      // Les lignes portent déjà le taux applicable : plus de surcharge ici,
      // donc un seul calcul possible.
      const totals = computeQuoteTotals(lines);

      return repo.save(ctx, {
        quoteId: input.quoteId,
        clientId: input.clientId,
        title: input.title,
        description: input.description ?? null,
        siteAddressLine1: input.siteAddressLine1 ?? null,
        sitePostalCode: input.sitePostalCode ?? null,
        siteCity: input.siteCity ?? null,
        validityDays: input.validityDays,
        estimatedDurationDays: input.estimatedDurationDays ?? null,
        appliesReducedTva: input.appliesReducedTva,
        reducedTvaRate: input.reducedTvaRate,
        attestationTva: input.attestationTva ?? null,
        paymentConditions: input.paymentConditions ?? null,
        warrantyText: input.warrantyText ?? null,
        specialConditions: input.specialConditions ?? null,
        observations: input.observations ?? null,
        lines,
        totals,
      });
    },
  });

  /**
   * Annoncer une date de début au client.
   *
   * Un verbe à part et non un champ de plus dans `enregistrer_devis` : annoncer
   * une disponibilité est un acte distinct de la rédaction du devis. Il se fait
   * après coup, sur un devis déjà chiffré, et il engage l'entreprise sur autre
   * chose que des prix.
   *
   * Effacer les deux dates est permis — passer `null` retire l'annonce — parce
   * qu'un planning bouge et qu'une date devenue fausse vaut moins que pas de
   * date du tout.
   */
  const annoncerDisponibilite = defineService<AnnounceAvailabilityInput, QuoteDetail>({
    name: "annoncer_disponibilite",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron", "conducteur", "assistant"],
    handler: (ctx, input) =>
      repo.setExpectedDates(ctx, input.quoteId, input.startsOn ?? null, input.endsOn ?? null),
  });

  const changerStatutDevis = defineService<ChangeQuoteStatusInput, QuoteDetail>({
    name: "changer_statut_devis",
    level: ActionLevel.ReversibleWrite,
    handler: async (ctx, input) => {
      if (input.status === "rejected" && !input.rejectionReason?.trim()) {
        throw new DomainError(
          "invalid_input",
          "Indiquez pourquoi le devis a été refusé : c'est cette donnée qui " +
            "permet de savoir si vous perdez sur le prix ou sur les délais.",
          { quoteId: input.quoteId },
        );
      }
      return repo.changeStatus(
        ctx,
        input.quoteId,
        input.status,
        input.rejectionReason ?? null,
      );
    },
  });

  return {
    listerDevis,
    listerDevisEnAttente,
    lireDevis,
    enregistrerDevis,
    changerStatutDevis,
    annoncerDisponibilite,
  };
}

export type QuoteServices = ReturnType<typeof createQuoteServices>;
